import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { History, Briefcase, ChevronDown, Check, Layers } from 'lucide-react';
import type {
  InventorySkuRow,
  InventoryTxnRow,
  InventoryTxnReason,
} from '@gutterinv/shared';
import { normalizeUom, computeSellPriceCents, isWholePurchaseUnit } from '@gutterinv/shared';
import { presetsForSkuCode } from '../lib/sku-line-presets.js';
import { uomDefaultsForCategory } from '../lib/uom-defaults.js';
import {
  getInventoryTxns,
  getSkuCategories,
  getSkuCommitments,
  postCountCorrection,
  patchSku,
} from '../api/client.js';
import { Badge } from './ui/badge.js';
import { Button } from './ui/button.js';
import { Input } from './ui/input.js';
import { Skeleton } from './ui/skeleton.js';
import {
  SheetBody,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from './ui/sheet.js';
import { cn } from '../lib/utils.js';
import { formatCents } from '../lib/format.js';
import { qk } from '../lib/queryKeys.js';
import { parseCoverageSfFromName } from '../lib/coverage-sf.js';
import { catalogColumnsForCategory } from '../pages/inventory-table-config.js';

function formatTxnTimestamp(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

// One value-column geometry shared by editable boxes AND read-only values, so that
// when rows stack vertically every digit-start and digit-end falls on the same x
// (not just the right edge). Each row's value side is: [prefix $][value w-20][suffix].
//   FIELD_BOX   — editable input (visible border + bg).
//   VALUE_CELL  — read-only value with the SAME w-20/px-2/py-1/1px-border geometry but
//                 INVISIBLE, so its digits inset exactly like a box's digits.
//   PREFIX_CELL — fixed $ slot ($ for Cost+Price align row-to-row; empty otherwise).
//   UOM_CELL    — fixed unit-suffix slot (/FT) to the right of the value column.
const FIELD_BOX =
  'tnum w-20 shrink-0 rounded border border-input bg-card px-2 py-1 text-right text-sm font-semibold text-foreground outline-none transition-colors focus:border-ring focus:ring-2 focus:ring-ring';
const VALUE_CELL =
  'tnum w-20 shrink-0 rounded border border-transparent px-2 py-1 text-right text-sm font-semibold text-foreground';
const PREFIX_CELL = 'w-3 shrink-0 text-right text-sm font-semibold text-muted-foreground';
const UOM_CELL = 'w-7 shrink-0 text-[0.625rem] uppercase tracking-wider text-taupe';
// One fixed row height so editable-box rows and read-only rows line up evenly.
const PANEL_ROW = 'flex h-10 items-center justify-between gap-3';

export function InventoryDetailPanel({
  sku,
  canSeeCost,
  canManageCategories,
  canEditReorder,
  onToggleActive,
}: {
  sku: InventorySkuRow;
  /** Cost line in the Stock section — owner+admin+back_office. Cost/price EDITING
   *  lives on the main page's Commercial view, not here. */
  canSeeCost: boolean;
  /** Category dropdown (reassign) — owner/admin/back_office (same as Manage Categories). */
  canManageCategories: boolean;
  /** Owner/admin/back_office may edit reorder point + coverage + conversion inline. */
  canEditReorder: boolean;
  /** Request an enable/disable for this SKU — the page opens the confirm modal +
   *  runs the (optimistic) bulk-active mutation. Omitted → no status control. */
  onToggleActive?: (sku: InventorySkuRow) => void;
}) {
  const queryClient = useQueryClient();

  // Categories list for the in-line "move SKU to another category" picker.
  const { data: categories } = useQuery({
    queryKey: ['sku-categories'],
    queryFn: getSkuCategories,
    enabled: canManageCategories,
    staleTime: 60_000,
  });

  const categoryMutation = useMutation({
    // Re-derive the base UNIT from the new category's defaults and send it in the SAME
    // patch — otherwise `unit` drifts from the category (root cause of KN3646's stale
    // unit='ea' after it was recategorized into Insulation, which should be 'bag').
    // unit ONLY: purchaseUom/consumptionUom may be deliberately customized per SKU, so
    // a category change must not clobber them — only the base unit tracks the category.
    mutationFn: (categoryId: string) => {
      const catName = categories?.find((c) => c.id === categoryId)?.name ?? '';
      return patchSku(sku.skuId, { categoryId, unit: uomDefaultsForCategory(catName).base });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['sku-categories'] });
    },
  });

  return (
    <>
      <SheetHeader>
        <div className="flex flex-wrap items-center gap-2 pr-10">
          {sku.isLowStock && <Badge variant="warning">Low stock</Badge>}
          {!sku.active && (
            <span className="rounded-sm border border-border bg-secondary px-2 py-0.5 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
              Disabled
            </span>
          )}
          {onToggleActive && (
            <Button
              size="sm"
              variant={sku.active ? 'outline' : 'default'}
              className="ml-auto"
              onClick={() => onToggleActive(sku)}
            >
              {sku.active ? 'Disable' : 'Enable'}
            </Button>
          )}
        </div>
        {/* Two labeled identifier lines (replacing the old bare SKU-code line):
            Item # = the Item-Reference code (skus.code); Part # = the Service
            Partners 12-digit Part No (migration 0073), hidden when null. Codes
            stay monospace via the shared `ticker` class. */}
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="w-12 shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
              Item #
            </span>
            <span className="ticker rounded-md">{sku.code}</span>
          </div>
          {sku.partNo && (
            <div className="flex items-center gap-2">
              <span className="w-12 shrink-0 text-[0.6875rem] font-semibold uppercase tracking-wider text-muted-foreground">
                Part #
              </span>
              <span className="ticker rounded-md">{sku.partNo}</span>
            </div>
          )}
        </div>
        <SheetTitle>
          {sku.name}
          {sku.variant && (
            <span className="text-base font-normal text-muted-foreground">
              {' · '}
              {sku.variant}
            </span>
          )}
        </SheetTitle>
        <SheetDescription>
          {canManageCategories && categories && categories.length > 0 ? (
            <span className="inline-flex items-center gap-1.5">
              <span className="text-[0.6875rem] uppercase tracking-wider text-taupe">
                Category
              </span>
              <select
                value={sku.categoryId}
                onChange={(e) => {
                  const next = e.target.value;
                  if (next && next !== sku.categoryId) {
                    categoryMutation.mutate(next);
                  }
                }}
                disabled={categoryMutation.isPending}
                className="h-7 rounded-md border border-input bg-background px-2 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
              {categoryMutation.error && (
                <span className="text-[0.625rem] text-destructive">
                  {categoryMutation.error instanceof Error
                    ? categoryMutation.error.message
                    : 'Failed'}
                </span>
              )}
            </span>
          ) : (
            sku.categoryName
          )}
        </SheetDescription>
      </SheetHeader>

      {/* Single scrolling detail (no tabs): Catalog parameters, then the
          operational sections (stock/adjust/coverage/conversion/committed) and
          History. Cost/price EDITING moved to the main page's Commercial view. */}
      <SheetBody className="space-y-6">
        <section>
          <h3 className="eyebrow mb-2">Catalog</h3>
          <CatalogTab sku={sku} />
        </section>
        <OperationalTab sku={sku} canSeeCost={canSeeCost} canEditReorder={canEditReorder} />
      </SheetBody>
    </>
  );
}

// ── Catalog tab ───────────────────────────────────────────────────────────────
// Per-category stored attributes, rendered read-only as a label/value list from
// the SAME ATTRIBUTE_TABLES descriptor that used to drive the wide table columns
// (resolved by category name → AttrSet). Catch-all categories (Accessories /
// Miscellaneous / Insulation Accessories / Safety-PPE) have no attribute schema →
// graceful empty state.
function CatalogTab({ sku }: { sku: InventorySkuRow }) {
  // Category-driven: the SAME per-category catalog columns the Catalog table view
  // uses (fields, labels, order) — not a hardcoded list. Empty for catch-all cats.
  const cols = catalogColumnsForCategory(sku.categoryName);
  if (cols.length === 0) {
    return (
      <div className="rounded-md border border-border bg-secondary/30 px-3 py-6 text-center text-sm text-muted-foreground">
        No catalog attributes for this category.
      </div>
    );
  }
  // Compact horizontal spec strip: one shaded band, attributes as side-by-side
  // label-over-value columns EVENLY distributed across the full width (N equal
  // fractional columns sized to the category's field count — no trailing right gap).
  return (
    <div
      className="grid gap-x-4 gap-y-2 rounded-md border border-border bg-secondary/30 px-3 py-2.5"
      style={{ gridTemplateColumns: `repeat(${cols.length}, minmax(0, 1fr))` }}
    >
      {cols.map((col) => (
        <div key={col.label} className="min-w-0">
          <div className="text-[0.6875rem] uppercase tracking-wider text-taupe">{col.label}</div>
          <div className="text-sm font-semibold text-foreground">{col.render(sku)}</div>
        </div>
      ))}
    </div>
  );
}

// ── Operational tab ───────────────────────────────────────────────────────────
// Stock + adjust-count + coverage + unit-conversion + committed-jobs + history.
// All the on-hand/operational surfaces; pricing lives in Commercial.
function OperationalTab({
  sku,
  canSeeCost,
  canEditReorder,
}: {
  sku: InventorySkuRow;
  canSeeCost: boolean;
  canEditReorder: boolean;
}) {
  const queryClient = useQueryClient();
  const [historyExpanded, setHistoryExpanded] = useState(true);
  const [reorderValue, setReorderValue] = useState(String(sku.reorderThreshold));
  const [reorderState, setReorderState] = useState<SaveState>('idle');

  // Inline Adjust On Hand: enter the NEW on-hand value (absolute set-to) + a REQUIRED
  // reason. Writes a count_correction movement (reason → notes) to History immediately.
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustValue, setAdjustValue] = useState('');
  const [adjustReason, setAdjustReason] = useState('');

  // Cost inline edit (was display-only here; editing previously lived only in
  // Commercial). Price is shown computed from the category markup (overrides stay
  // in Commercial). markupPct resolved from the categories list.
  const [costValue, setCostValue] = useState(sku.costCents != null ? (sku.costCents / 100).toFixed(2) : '');
  const [costState, setCostState] = useState<SaveState>('idle');

  useEffect(() => {
    setReorderValue(String(sku.reorderThreshold));
  }, [sku.skuId, sku.reorderThreshold]);
  useEffect(() => {
    setCostValue(sku.costCents != null ? (sku.costCents / 100).toFixed(2) : '');
  }, [sku.skuId, sku.costCents]);
  useEffect(() => {
    setAdjustOpen(false);
    setAdjustValue('');
    setAdjustReason('');
  }, [sku.skuId]);

  const { data: txns, isLoading: txnsLoading } = useQuery({
    queryKey: qk.inventory.txns(sku.skuId),
    queryFn: () => getInventoryTxns(sku.skuId, 50),
  });

  const { data: markupCategories } = useQuery({
    queryKey: ['sku-categories'],
    queryFn: getSkuCategories,
    staleTime: 60_000,
  });
  const markupPct = markupCategories?.find((c) => c.id === sku.categoryId)?.defaultMarkupPct ?? null;
  // Cost→price math + the live edit-preview values (Price Row 2, the conversion
  // equation Row 3, and the visual bar Row 4) are computed further below, AFTER the
  // Cost + Factor edit state is declared — they read the in-progress typed value so
  // the block previews before save (see liveCostCents / liveFactor).

  const correctMutation = useMutation({
    mutationFn: postCountCorrection,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: qk.inventory.txns(sku.skuId) });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.today() });
      setAdjustOpen(false);
      setAdjustValue('');
      setAdjustReason('');
    },
  });

  // actualOnHand = the entered new on-hand value (absolute). Reason is required.
  const adjustTarget = Number(adjustValue);
  const adjustValid =
    adjustValue.trim() !== '' && Number.isFinite(adjustTarget) && adjustTarget >= 0 && adjustReason.trim() !== '';
  const handleAdjust = () => {
    if (!adjustValid) return;
    correctMutation.mutate({ skuId: sku.skuId, actualOnHand: adjustTarget, notes: adjustReason.trim() });
  };

  const costMutation = useMutation({
    mutationFn: (val: number) => patchSku(sku.skuId, { costCents: val }),
    onSuccess: () => {
      setCostState('saved');
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      setTimeout(() => setCostState('idle'), 1200);
    },
    onError: () => {
      setCostState('error');
      setTimeout(() => setCostState('idle'), 2000);
    },
  });
  const saveCost = () => {
    const trimmed = costValue.trim();
    if (trimmed === '') {
      setCostValue(sku.costCents != null ? (sku.costCents / 100).toFixed(2) : '');
      return;
    }
    const parsed = Math.round(Number(trimmed) * 100);
    if (Number.isNaN(parsed) || parsed < 0) {
      setCostState('error');
      setTimeout(() => setCostState('idle'), 2000);
      return;
    }
    if (parsed === sku.costCents) return;
    setCostState('saving');
    costMutation.mutate(parsed);
  };

  // Conversion Factor inline edit (lifted from the former UnitConversionSection so
  // the Pricing card can show it as a flat row, not a nested shaded sub-box).
  const [factorValue, setFactorValue] = useState(
    sku.conversionFactor != null ? String(sku.conversionFactor) : '',
  );
  const [factorState, setFactorState] = useState<SaveState>('idle');
  useEffect(() => {
    setFactorValue(sku.conversionFactor != null ? String(sku.conversionFactor) : '');
  }, [sku.skuId, sku.conversionFactor]);
  const factorMutation = useMutation({
    mutationFn: (val: number) =>
      patchSku(sku.skuId, { conversionFactor: val, conversionFactorSource: 'override' }),
    onSuccess: () => {
      setFactorState('saved');
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      setTimeout(() => setFactorState('idle'), 1200);
    },
    onError: () => {
      setFactorState('error');
      setTimeout(() => setFactorState('idle'), 2000);
    },
  });
  const saveFactor = () => {
    const trimmed = factorValue.trim();
    if (trimmed === '') {
      setFactorValue(sku.conversionFactor != null ? String(sku.conversionFactor) : '');
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setFactorState('error');
      setTimeout(() => setFactorState('idle'), 2000);
      return;
    }
    if (parsed === sku.conversionFactor) return;
    setFactorState('saving');
    factorMutation.mutate(parsed);
  };

  // Live edit-preview values: while the Cost / Factor inputs hold an in-progress
  // (unsaved) edit, derive Price (Row 2), the conversion equation (Row 3) and the
  // visual bar (Row 4) from the typed value; fall back to the persisted SKU value
  // when the input is empty/invalid. Nothing here persists — save still runs on
  // blur/Enter via saveCost / saveFactor (unchanged).
  const liveCostCents = (() => {
    const t = costValue.trim();
    if (t === '') return sku.costCents;
    const parsed = Math.round(Number(t) * 100);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : sku.costCents;
  })();
  const liveFactor = (() => {
    const t = factorValue.trim();
    if (t === '') return sku.conversionFactor;
    const parsed = Number(t);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : sku.conversionFactor;
  })();
  // Shared cost→price math: cost (per purchaseUom) ÷ conversionFactor → price (per
  // consumptionUom), then markup. Whole-bag insulation (coverageSf > 0) not divided.
  // Identical to the worker engine + the inventory grid — now fed the live edit
  // values so Price (Row 2) previews before save.
  const computedPriceCents =
    liveCostCents != null && markupPct != null
      ? computeSellPriceCents(
          {
            costCents: liveCostCents,
            conversionFactor: liveFactor,
            isWholeUnitPurchase: isWholePurchaseUnit(sku),
          },
          markupPct,
        ).sellPriceCents
      : null;
  // Conversion display units (Row 3) read the commercial Buy / Sell UoMs, falling
  // back to the base unit for non-commercial SKUs. Price (Row 2) keeps its own
  // /unit suffix unchanged.
  const buyUom = normalizeUom(sku.purchaseUom) || normalizeUom(sku.unit);
  // Shared-bag preset SKUs (e.g. KN3646) have no single sell UOM/coverage — they sell
  // by SF with a per-R-value coverage. Display-only: show Sell = SF and the conversion
  // as "varies by R-value" (the data stays BAG so the estimate's per-unit price label
  // stays correct — see /architect). The per-bag Price row ($/bag) is left as-is.
  const isPresetSku = presetsForSkuCode(sku.code).length > 0;
  const sellUom = isPresetSku ? 'SF' : normalizeUom(sku.consumptionUom) || normalizeUom(sku.unit);

  // One-click "accept proposed pack qty" (unchanged behavior, flat presentation).
  const proposedQty =
    sku.uomStatus === 'PROPOSED_PACK_QTY_REVIEW' && sku.packQtyProposed != null && sku.packQtyProposed > 0
      ? sku.packQtyProposed
      : null;
  const proposedUnit = normalizeUom(sku.packUnitProposed) || 'PC';

  // Reorder-point inline edit — mirrors the list row. Also invalidate
  // dashboard.today() so the Today Low-Inventory card + bell refresh.
  const reorderMutation = useMutation({
    mutationFn: (val: number) => patchSku(sku.skuId, { reorderThreshold: val }),
    onSuccess: () => {
      setReorderState('saved');
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      void queryClient.invalidateQueries({ queryKey: qk.inventory.skus() });
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.today() });
      setTimeout(() => setReorderState('idle'), 1200);
    },
    onError: () => {
      setReorderState('error');
      setTimeout(() => setReorderState('idle'), 2000);
    },
  });

  const saveReorder = () => {
    const trimmed = reorderValue.trim();
    if (trimmed === '') {
      setReorderValue(String(sku.reorderThreshold));
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) {
      setReorderState('error');
      setTimeout(() => setReorderState('idle'), 2000);
      return;
    }
    if (parsed === sku.reorderThreshold) return;
    setReorderState('saving');
    reorderMutation.mutate(parsed);
  };

  // --- Coverage (SF/bag) — batting SKUs only ---
  const nameSuggestedSf = parseCoverageSfFromName(sku.name);
  const isBattingCandidate = sku.coverageSf != null || nameSuggestedSf != null;
  const [coverageValue, setCoverageValue] = useState(
    sku.coverageSf != null ? String(sku.coverageSf) : '',
  );
  const [coverageState, setCoverageState] = useState<SaveState>('idle');
  useEffect(() => {
    setCoverageValue(sku.coverageSf != null ? String(sku.coverageSf) : '');
  }, [sku.skuId, sku.coverageSf]);
  const coverageMutation = useMutation({
    mutationFn: (val: number | null) => patchSku(sku.skuId, { coverageSf: val }),
    onSuccess: () => {
      setCoverageState('saved');
      void queryClient.invalidateQueries({ queryKey: ['inventory'] });
      void queryClient.invalidateQueries({ queryKey: ['skus'] });
      setTimeout(() => setCoverageState('idle'), 1200);
    },
    onError: () => {
      setCoverageState('error');
      setTimeout(() => setCoverageState('idle'), 2000);
    },
  });
  const saveCoverage = () => {
    const trimmed = coverageValue.trim();
    if (trimmed === '') {
      const suggested = parseCoverageSfFromName(sku.name);
      if (suggested != null) {
        if (sku.coverageSf === suggested) {
          setCoverageValue(String(suggested));
          return;
        }
        setCoverageState('saving');
        coverageMutation.mutate(suggested);
      } else if (sku.coverageSf != null) {
        setCoverageState('saving');
        coverageMutation.mutate(null);
      }
      return;
    }
    const parsed = Number(trimmed);
    if (!Number.isFinite(parsed) || parsed < 0) {
      setCoverageState('error');
      setTimeout(() => setCoverageState('idle'), 2000);
      return;
    }
    if (parsed === sku.coverageSf) return;
    setCoverageState('saving');
    coverageMutation.mutate(parsed);
  };

  return (
    <div className="space-y-6">
      {/* Edit hub — Stock (left) + Pricing & conversion (right), side-by-side + equal
          height. Two-column is driven by the PANEL's width via a container query
          (.edit-hub-*), NOT a viewport breakpoint — the panel is a ~920px side sheet,
          so `lg:` (window ≥1024px) never fired and the cards always stacked. */}
      <div className="edit-hub-container">
      <div className="edit-hub-grid">
        {/* STOCK — labeled rows in a white card; section label above. Right values +
            UoM suffixes + the editable field box share fixed-width slots (w-20 value
            / w-7 UoM) so they align with the Pricing card's boxes. */}
        <div className="flex h-full flex-col">
          <div className="eyebrow mb-1.5 px-0.5">Stock</div>
          <section className="flex flex-1 flex-col rounded-lg border border-border bg-white p-3 shadow-sm">
            <dl className="divide-y divide-border/60">
              <div className={PANEL_ROW}>
                <dt className="eyebrow">On hand</dt>
                <dd className="flex items-center gap-1">
                  <span className={VALUE_CELL}>{sku.onHand.toLocaleString()}</span>
                  <span className={UOM_CELL}>{normalizeUom(sku.unit)}</span>
                </dd>
              </div>
              <div className={PANEL_ROW}>
                <dt className="eyebrow">Committed</dt>
                <dd className="flex items-center gap-1">
                  <span className={VALUE_CELL}>{sku.committedToQuotes.toLocaleString()}</span>
                  <span className={UOM_CELL}>{normalizeUom(sku.unit)}</span>
                </dd>
              </div>
              <div className={PANEL_ROW}>
                <dt className="eyebrow">Free</dt>
                <dd className="flex items-center gap-1">
                  <span
                    className={cn(
                      VALUE_CELL,
                      sku.effectivelyFree < 0 ? 'text-destructive' : sku.isLowStock ? 'text-warning' : 'text-success',
                    )}
                  >
                    {sku.effectivelyFree.toLocaleString()}
                  </span>
                  <span className={UOM_CELL}>{normalizeUom(sku.unit)}</span>
                </dd>
              </div>
              <div className={PANEL_ROW}>
                <dt className="eyebrow">Reorder at</dt>
                <dd className="flex items-center gap-1">
                  {canEditReorder ? (
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={reorderValue}
                      onChange={(e) => setReorderValue(e.target.value)}
                      onBlur={saveReorder}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          saveReorder();
                          e.currentTarget.blur();
                        }
                      }}
                      className={cn(
                        FIELD_BOX,
                        reorderState === 'error' && 'border-destructive',
                        reorderState === 'saved' && 'border-success ring-2 ring-success',
                      )}
                    />
                  ) : (
                    <span className={VALUE_CELL}>{sku.reorderThreshold.toLocaleString()}</span>
                  )}
                  <span className={UOM_CELL}>{normalizeUom(sku.unit)}</span>
                </dd>
              </div>
            </dl>
            {/* Adjust on hand — a text action beneath the rows; expands the inline
                set-to / +/- form (required reason → count_correction → History). */}
            {canEditReorder && (
              <div className="mt-auto pt-3">
                {adjustOpen ? (
                  <div className="space-y-2 rounded-md border border-border bg-secondary/30 p-2.5">
                    <div className="flex items-center gap-1.5">
                      <span className="eyebrow">New on-hand</span>
                      <input
                        type="number"
                        step="any"
                        value={adjustValue}
                        onChange={(e) => setAdjustValue(e.target.value)}
                        placeholder="0"
                        className={FIELD_BOX}
                        autoFocus
                        disabled={correctMutation.isPending}
                      />
                      <span className={UOM_CELL}>{normalizeUom(sku.unit)}</span>
                    </div>
                    <Input
                      value={adjustReason}
                      onChange={(e) => setAdjustReason(e.target.value)}
                      placeholder="Reason (required)"
                      className="h-8"
                      disabled={correctMutation.isPending}
                    />
                    <div className="flex items-center gap-2">
                      <Button size="sm" onClick={handleAdjust} disabled={!adjustValid || correctMutation.isPending}>
                        {correctMutation.isPending ? 'Saving…' : 'Save adjustment'}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setAdjustOpen(false)} disabled={correctMutation.isPending}>
                        Cancel
                      </Button>
                    </div>
                    {correctMutation.error && (
                      <div className="text-xs text-destructive">
                        {correctMutation.error instanceof Error ? correctMutation.error.message : 'Failed'}
                      </div>
                    )}
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAdjustOpen(true)}
                    className="text-xs font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
                  >
                    Adjust on hand
                  </button>
                )}
              </div>
            )}
          </section>
        </div>

        {/* PRICING & CONVERSION — flat rows in a white card; section label above.
            Cost/Factor field boxes are w-20 (same as Stock's Reorder box) so all
            editable boxes + values + UoM suffixes align across the two cards. */}
        <div className="flex h-full flex-col">
          <div className="eyebrow mb-1.5 flex items-center gap-1.5 px-0.5">
            Pricing &amp; conversion
            {sku.conversionFactorSource === 'override' && (
              <span className="rounded-sm border border-warning/40 bg-warning/10 px-1 text-[0.625rem] font-semibold normal-case tracking-normal text-warning-foreground">
                override
              </span>
            )}
          </div>
          <section className="flex flex-1 flex-col rounded-lg border border-border bg-white p-3 shadow-sm">
            <dl className="divide-y divide-border/60">
              {canSeeCost && (
                <>
                  <div className={PANEL_ROW}>
                    <dt className="eyebrow">Cost</dt>
                    <dd className="flex items-center gap-1">
                      <span className={PREFIX_CELL}>$</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={costValue}
                        onChange={(e) => setCostValue(e.target.value)}
                        onBlur={saveCost}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            saveCost();
                            e.currentTarget.blur();
                          }
                        }}
                        className={cn(FIELD_BOX, costState === 'error' && 'border-destructive', costState === 'saved' && 'border-success ring-2 ring-success')}
                      />
                      {/* COST is per the BUY unit (purchaseUom, e.g. LB for coil), not the
                          base count/sell unit. Fall back to the base unit when no commercial
                          Buy UoM is set (non-commercial SKUs unchanged). */}
                      <span className={UOM_CELL}>/{normalizeUom(sku.purchaseUom) || normalizeUom(sku.unit)}</span>
                    </dd>
                  </div>
                  {/* Shared-bag preset SKUs (KN3646) have NO single sell price — it's
                      per-R-value $/SF via the 7 presets. Suppress the single $/unit row
                      (would otherwise show a misleading "$51.80 /BAG"); Cost stays. */}
                  {!isPresetSku && (
                  <div className={PANEL_ROW}>
                    <dt className="eyebrow">
                      Price{markupPct != null && <span className="font-normal normal-case tracking-normal text-taupe"> · {markupPct}% markup</span>}
                    </dt>
                    <dd className="flex items-center gap-1">
                      <span className={PREFIX_CELL}>$</span>
                      <span className={VALUE_CELL}>
                        {computedPriceCents != null
                          ? (computedPriceCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : '—'}
                      </span>
                      <span className={UOM_CELL}>/{normalizeUom(sku.unit)}</span>
                    </dd>
                  </div>
                  )}
                </>
              )}
              {/* CONVERSION — the former Buy / Sell / Factor rows collapsed into one
                  equation: 1 {buyUom} → {factor} {sellUom}. Only the factor is
                  editable. The factor sits in the SAME value column (w-20, right-aligned)
                  as Cost's 3.98 and Price's 2.48, and the consumption unit sits in the
                  SAME unit column (w-7) as /LB and /FT — but renders as a plain quantity
                  (no leading slash) since it is a count, not a per-unit rate. The
                  "1 {buyUom} →" prefix is muted left-side context. */}
              <div className={PANEL_ROW}>
                <dt className="eyebrow">Conversion</dt>
                <dd className="flex items-center gap-1">
                  <span className="shrink-0 whitespace-nowrap text-[0.6875rem] uppercase tracking-wider text-taupe">
                    1 {buyUom || '—'} →
                  </span>
                  {isPresetSku ? (
                    <span className="text-[0.8125rem] text-foreground">
                      varies (by R-value)
                    </span>
                  ) : (
                    <>
                      {canEditReorder ? (
                        <input
                          type="number"
                          min="0"
                          step="any"
                          value={factorValue}
                          placeholder="—"
                          onChange={(e) => setFactorValue(e.target.value)}
                          onBlur={saveFactor}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              saveFactor();
                              e.currentTarget.blur();
                            }
                          }}
                          className={cn(
                            FIELD_BOX,
                            factorState === 'error' && 'border-destructive',
                            factorState === 'saved' && 'border-success ring-2 ring-success',
                          )}
                        />
                      ) : (
                        <span className={VALUE_CELL}>
                          {sku.conversionFactor != null ? sku.conversionFactor : '—'}
                        </span>
                      )}
                      <span className={UOM_CELL}>{sellUom || '—'}</span>
                    </>
                  )}
                </dd>
              </div>
            </dl>
            {canEditReorder && proposedQty != null && (
              <button
                type="button"
                disabled={factorState === 'saving'}
                onClick={() => {
                  setFactorState('saving');
                  factorMutation.mutate(proposedQty);
                }}
                className="mt-2 inline-flex items-center gap-1.5 self-start rounded-md border border-primary/40 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition-colors hover:bg-primary/20 disabled:opacity-50"
              >
                Use proposed ({proposedQty} {proposedUnit}/box)
              </button>
            )}
          </section>
        </div>
      </div>
      </div>

      {isBattingCandidate && (
        <section>
          <div className="rounded-md border border-primary/30 bg-primary/5 p-3">
            <div className="eyebrow mb-1 flex items-center gap-1.5 text-primary">
              <Layers className="h-3 w-3" />
              Coverage
            </div>
            {canEditReorder ? (
              <div className="flex items-baseline gap-2">
                <div className="flex items-center gap-1">
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={coverageValue}
                    placeholder={nameSuggestedSf != null ? String(nameSuggestedSf) : ''}
                    onChange={(e) => setCoverageValue(e.target.value)}
                    onBlur={saveCoverage}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        saveCoverage();
                        e.currentTarget.blur();
                      }
                    }}
                    className={cn(
                      'tnum w-24 rounded border border-transparent bg-transparent text-xl font-bold leading-tight text-foreground outline-none transition-colors hover:border-input focus:border-input focus:ring-2 focus:ring-ring',
                      coverageState === 'error' && 'ring-2 ring-destructive',
                      coverageState === 'saved' && 'ring-2 ring-success',
                    )}
                  />
                  {coverageState === 'saved' && (
                    <Check className="h-3.5 w-3.5 shrink-0 text-success animate-in fade-in" />
                  )}
                </div>
                <span className="text-[0.6875rem] uppercase tracking-wider text-taupe">
                  SF / bag
                </span>
              </div>
            ) : (
              <div className="flex items-baseline gap-2">
                <span className="tnum text-xl font-bold text-foreground">
                  {sku.coverageSf != null ? sku.coverageSf.toLocaleString() : '—'}
                </span>
                <span className="text-[0.6875rem] uppercase tracking-wider text-taupe">
                  SF / bag
                </span>
              </div>
            )}
            <p className="mt-1 text-xs text-muted-foreground">
              Drives insulation labor footage.
            </p>
          </div>
        </section>
      )}

      {sku.committedToQuotes > 0 && (
        <CommittedJobsSection skuId={sku.skuId} unit={sku.unit} />
      )}

      <section>
        <button
          type="button"
          onClick={() => setHistoryExpanded((v) => !v)}
          className="mb-2 flex w-full items-center gap-1.5 text-left"
          aria-expanded={historyExpanded}
        >
          <History className="h-3 w-3" />
          <span className="eyebrow">History</span>
          {txns && <span className="eyebrow text-taupe">({txns.length})</span>}
          <ChevronDown
            className={cn(
              'ml-auto h-3 w-3 shrink-0 text-taupe transition-transform',
              historyExpanded ? '' : '-rotate-90',
            )}
          />
        </button>
        {historyExpanded && (
          <>
            {txnsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
                <Skeleton className="h-12" />
              </div>
            ) : !txns || txns.length === 0 ? (
              <div className="rounded-md border border-border bg-secondary/30 px-3 py-6 text-center text-sm text-muted-foreground">
                No transactions yet
              </div>
            ) : (
              <div className="divide-y divide-border rounded-md border border-border">
                {txns.map((t) => (
                  <TxnRow key={t.id} txn={t} unit={sku.unit} canSeeCost={canSeeCost} />
                ))}
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}

function CommittedJobsSection({ skuId, unit }: { skuId: string; unit: string }) {
  const { data: commitments, isLoading } = useQuery({
    queryKey: ['inventory', 'commitments', skuId],
    queryFn: () => getSkuCommitments(skuId),
  });

  return (
    <section>
      <h3 className="eyebrow mb-2 flex items-center gap-1.5">
        <Briefcase className="h-3 w-3" />
        Committed to jobs
      </h3>
      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : !commitments || commitments.length === 0 ? (
        <div className="rounded-md border border-border bg-secondary/30 px-3 py-6 text-center text-sm text-muted-foreground">
          No committed jobs
        </div>
      ) : (
        <div className="divide-y divide-border rounded-md border border-border">
          {commitments.map((c) => (
            <div
              key={c.jobId}
              className="flex items-start justify-between gap-3 px-3 py-2.5"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground">
                  {c.jobDisplayName}
                </div>
                <div className="text-xs text-muted-foreground">{c.customerName}</div>
              </div>
              <div className="tnum text-sm font-semibold text-foreground whitespace-nowrap">
                {c.quantity.toLocaleString()} {unit}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function TxnRow({
  txn,
  unit,
  canSeeCost,
}: {
  txn: InventoryTxnRow;
  unit: string;
  canSeeCost: boolean;
}) {
  const positive = txn.delta > 0;
  return (
    <div className="flex items-start justify-between gap-3 px-3 py-2.5">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <ReasonBadge reason={txn.reason} />
          <span
            className={cn(
              'tnum text-sm font-semibold',
              positive ? 'text-foreground' : 'text-destructive',
            )}
          >
            {positive ? '+' : ''}
            {txn.delta.toLocaleString()} {unit}
          </span>
        </div>
        <div className="mt-0.5 text-xs text-taupe">
          {formatTxnTimestamp(txn.createdAt)}
          {txn.createdByEmail && (
            <>
              <span className="mx-1.5">·</span>
              {txn.createdByEmail}
            </>
          )}
        </div>
        {txn.notes && (
          <div className="mt-1 text-xs text-muted-foreground">{txn.notes}</div>
        )}
      </div>
      {canSeeCost && txn.unitCostCents !== null && (
        <div className="text-right">
          <div className="text-[0.6875rem] uppercase tracking-wider text-taupe">
            unit cost
          </div>
          <div className="tnum text-xs text-foreground">
            {formatCents(txn.unitCostCents)}
          </div>
        </div>
      )}
    </div>
  );
}

function ReasonBadge({ reason }: { reason: InventoryTxnReason }) {
  const variant: 'default' | 'secondary' | 'destructive' | 'warning' | 'outline' =
    reason === 'receipt'
      ? 'default'
      : reason === 'pull' || reason === 'install'
        ? 'secondary' // consumption (legacy 'pull' + current 'install')
        : reason === 'return' || reason === 'uninstall'
          ? 'outline' // reversal (legacy 'return' + current 'uninstall')
          : reason === 'damage'
            ? 'destructive'
            : 'warning'; // count_correction
  const label = reason.replace('_', ' ');
  return (
    <Badge variant={variant} className="capitalize">
      {label}
    </Badge>
  );
}
